import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { EVENT } from '@/common/constants';
import { useEventAgent } from '@/common/hooks/useEventAgent';
import { NotificationWrapper } from '@/components/notification';
import { Button } from '@hotmart-org-ca/user-account-ui';

interface BrowserIncompatibleProps {
  onRetry?: () => void;
  lastFailureReason?: string;
  initializationAttempts?: number;
}

function BrowserIncompatible({
  onRetry,
  lastFailureReason,
  initializationAttempts = 0,
}: BrowserIncompatibleProps) {
  const { trackEventWithDedup } = useEventAgent();
  const hasTrackedPageView = useRef(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!hasTrackedPageView.current) {
      trackEventWithDedup(EVENT.SDK_INITIALIZATION_ERROR);
      trackEventWithDedup(EVENT.NOTIFICATION_TRY_AGAIN_VIEW);
      hasTrackedPageView.current = true;
    }
  }, [trackEventWithDedup]);

  const handleRetry = () => {
    trackEventWithDedup(EVENT.NOTIFICATION_ERROR_RETRY_CLICK);
    onRetry?.();
  };

  const handleRefresh = () => {
    trackEventWithDedup(EVENT.NOTIFICATION_ERROR_RETRY_CLICK);
    window.location.reload();
  };

  // Extrair informações específicas do Chrome Mobile iOS
  const isChromeMobileIOS = lastFailureReason?.includes('Chrome Mobile iOS');
  const browserInfo = isChromeMobileIOS
    ? (() => {
        const ua = navigator.userAgent;
        const versionMatch = ua.match(/Chrome\/([\d.]+)/);
        return {
          browser: 'Chrome Mobile',
          version: versionMatch ? versionMatch[1] : 'unknown',
          os: 'iOS',
          platform: /iPhone/.test(ua) ? 'iPhone' : 'iPad',
        };
      })()
    : null;

  return (
    <NotificationWrapper
      title={t('browserIncompatible.title', 'Browser Incompatível')}
      help={t(
        'browserIncompatible.help',
        'Seu navegador não é compatível com a verificação biométrica'
      )}
      status="warning"
    >
      <div className="w-full space-y-4">
        {browserInfo && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="text-sm text-red-800">
              <div className="mb-2 font-semibold">
                {t('browserIncompatible.detectedBrowser', 'Browser Detectado:')}
              </div>
              <div>
                📱 {browserInfo.browser} {browserInfo.version}
              </div>
              <div>
                📋 {browserInfo.os} ({browserInfo.platform})
              </div>
              <div className="mt-2 text-xs text-red-600">
                {t(
                  'browserIncompatible.unsupportedVersion',
                  'Esta versão específica não é suportada pelo sistema de verificação biométrica.'
                )}
              </div>
            </div>
          </div>
        )}

        {/* Detalhes Técnicos */}
        {lastFailureReason && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs text-gray-600">
              <div className="mb-1 font-semibold">
                {t('browserIncompatible.technicalDetails', 'Detalhes Técnicos:')}
              </div>
              <div className="break-words">{lastFailureReason}</div>
              {initializationAttempts > 0 && (
                <div className="mt-1">
                  {t(
                    'browserIncompatible.initializationAttempts',
                    'Tentativas de inicialização: {{count}}',
                    { count: initializationAttempts }
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="text-left">
          <div className="mb-2 text-sm font-medium text-gray-700">
            {t('browserIncompatible.recommendedSolutions', '💡 Soluções Recomendadas:')}
          </div>
          <ul className="space-y-1 pl-4 text-sm text-gray-600">
            {isChromeMobileIOS ? (
              <>
                <li>
                  •{' '}
                  {t(
                    'browserIncompatible.solutions.chrome.useSafari',
                    'Use o Safari no iOS em vez do Chrome'
                  )}
                </li>
                <li>
                  •{' '}
                  {t(
                    'browserIncompatible.solutions.chrome.updateChrome',
                    'Atualize para uma versão mais recente do Chrome'
                  )}
                </li>
                <li>
                  •{' '}
                  {t(
                    'browserIncompatible.solutions.chrome.updateiOS',
                    'Verifique se há atualizações do iOS disponíveis'
                  )}
                </li>
                <li>
                  •{' '}
                  {t(
                    'browserIncompatible.solutions.chrome.useDesktop',
                    'Acesse pelo computador desktop'
                  )}
                </li>
              </>
            ) : (
              <>
                <li>
                  •{' '}
                  {t(
                    'browserIncompatible.solutions.general.useSupportedBrowser',
                    'Use um navegador suportado (Safari, Firefox, Edge)'
                  )}
                </li>
                <li>
                  •{' '}
                  {t(
                    'browserIncompatible.solutions.general.updateBrowser',
                    'Atualize seu navegador para a versão mais recente'
                  )}
                </li>
                <li>
                  •{' '}
                  {t(
                    'browserIncompatible.solutions.general.enableJavaScript',
                    'Verifique se JavaScript está habilitado'
                  )}
                </li>
                <li>
                  •{' '}
                  {t(
                    'browserIncompatible.solutions.general.clearCache',
                    'Limpe o cache do navegador'
                  )}
                </li>
              </>
            )}
          </ul>
        </div>

        <div className="flex flex-col gap-3">
          {onRetry && (
            <Button color="primary" onClick={handleRetry} fullWidth>
              {t('browserIncompatible.buttons.tryAgain', 'Tentar Novamente')}
            </Button>
          )}

          <Button color="secondary" onClick={handleRefresh} fullWidth>
            {t('browserIncompatible.buttons.reloadPage', 'Recarregar Página')}
          </Button>

          {process.env.NODE_ENV === 'development' && (
            <Button
              color="tertiary"
              onClick={() => {
                console.log('🔍 Browser Incompatible Debug Info:', {
                  lastFailureReason,
                  initializationAttempts,
                  userAgent: navigator.userAgent,
                  timestamp: new Date().toISOString(),
                  isChromeMobileIOS,
                  browserInfo,
                });
              }}
              fullWidth
            >
              {t('browserIncompatible.buttons.debugInfo', 'Debug Info (Dev)')}
            </Button>
          )}
        </div>

        {/* Contato de Suporte */}
        <div className="border-t border-gray-200 pt-4 text-center">
          <div className="text-xs text-gray-500">
            {t(
              'browserIncompatible.supportContact',
              'Precisa de ajuda? Entre em contato com o suporte'
            )}
          </div>
        </div>
      </div>
    </NotificationWrapper>
  );
}

export default BrowserIncompatible;
